/**
 * Content Unit - Lightweight Ad Serving Script
 * Version: __VERSION__
 */

// Type definitions
interface Creative {
  width: number | string;
  height: number;
  imageUrl: string;
  clickUrl: string;
  alt?: string;
}

interface ContentUnit {
  slot: string;
  creatives: Creative[];
  autoAppendPublisher?: boolean;
  passthroughParams?: string[];
}

interface Settings {
  lazyLoad: boolean;
  lazyLoadMargin?: number;
  passthroughParams?: string[];
}

interface Config {
  units: ContentUnit[];
  settings: Settings;
}

interface ParsedSlot {
  element: HTMLElement;
  slot: string;
  format: string;
  width: string | null;
  height: string | null;
  responsive: boolean;
}

interface ContentUnitLoadedDetail {
  slot: string;
  unit: ContentUnit;
  creative?: Creative;
}

interface ContentUnitClickedDetail {
  slot: string;
  creative: Creative;
}

(function(window: Window, document: Document): void {
  'use strict';

  // Global initialization guard to prevent multiple script loads
  if (window.__contentUnitsInitialized__) {
    if (__DEV__) {
      console.warn('Content Unit: Already initialized (script loaded multiple times). Ignoring duplicate script load.');
    }
    return;
  }
  window.__contentUnitsInitialized__ = true;

  // Constants
  const DEFAULT_WIDTH = 300;
  const DEFAULT_HEIGHT = 250;
  const DEFAULT_LAZY_LOAD_MARGIN = 200;
  const SLOT_SELECTOR = '.content-unit:not([data-content-loaded])';
  const IMAGE_RETRY_MAX = 2;
  const IMAGE_RETRY_DELAY_MS = 1000;
  const CONFIG_FETCH_RETRIES = 3;
  const CONFIG_FETCH_BASE_DELAY = 500;

  /**
   * Gets the base URL for config files, relative to the script's location
   * Falls back to __CONFIG_BASE_URL__ if script location cannot be determined
   */
  function getConfigBaseUrl(): string {
    try {
      // Get the currently executing script's URL
      const scripts = document.getElementsByTagName('script');
      for (let i = scripts.length - 1; i >= 0; i--) {
        const src = scripts[i].src;
        if (src && (src.includes('cu.js') || src.includes('maven-ads'))) {
          // Extract the directory path from the script URL
          const scriptUrl = new URL(src);
          const pathParts = scriptUrl.pathname.split('/');
          pathParts.pop(); // Remove the filename
          return scriptUrl.origin + pathParts.join('/');
        }
      }
    } catch (e) {
      if (__DEV__) console.warn('Content Unit: Could not determine script location', e);
    }
    // Fallback to build-time config
    return __CONFIG_BASE_URL__;
  }

  const CONFIG_BASE_URL = getConfigBaseUrl();

  /**
   * Validates that a URL uses a safe protocol (http or https)
   * @param {string} url - The URL to validate
   * @returns {boolean} True if URL is safe to use
   */
  function isValidUrl(url: string): boolean {
    if (!url || typeof url !== 'string') {
      return false;
    }
    try {
      const parsed = new URL(url, window.location.href);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch (_e) {
      return false;
    }
  }

  /**
   * Sanitizes text to prevent injection attacks
   * @param {string} text - The text to sanitize
   * @returns {string} Sanitized text
   */
  function sanitizeText(text: string): string {
    if (!text || typeof text !== 'string') {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = text;
    return div.textContent || '';
  }

  /**
   * Clears all child elements from a DOM element
   * @param {HTMLElement} el - The element to clear
   */
  function clearElement(el: HTMLElement): void {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }

  /**
   * Formats a dimension value for CSS (adds 'px' suffix if numeric)
   * @param {number | string} value - The dimension value
   * @returns {string} Formatted dimension string
   */
  function formatDimension(value: number | string): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  /**
   * Validates a slot ID to ensure it's safe and properly formatted.
   * Slot IDs should be UUID-like strings or alphanumeric identifiers.
   * @param {string | undefined} slotId - The slot ID to validate
   * @returns {string | null} The validated slot ID or null if invalid
   */
  function validateSlotId(slotId: string | undefined): string | null {
    if (!slotId || typeof slotId !== 'string') {
      return null;
    }

    // Trim whitespace
    const trimmed = slotId.trim();

    // Reject empty strings
    if (trimmed.length === 0) {
      return null;
    }

    // Reject excessively long slot IDs (prevent DoS)
    if (trimmed.length > 256) {
      if (__DEV__) console.warn('Content Unit: Slot ID exceeds maximum length');
      return null;
    }

    // Slot IDs should only contain safe characters:
    // alphanumeric, hyphens, underscores (UUID-compatible)
    const safePattern = /^[a-zA-Z0-9_-]+$/;
    if (!safePattern.test(trimmed)) {
      if (__DEV__) console.warn('Content Unit: Slot ID contains invalid characters', { slotId: trimmed });
      return null;
    }

    return trimmed;
  }

  /**
   * Appends publisher parameter to a URL if not already present
   * @param {string} url - The URL to append the parameter to
   * @returns {string} URL with publisher parameter appended
   */
  function appendPublisherParam(url: string): string {
    if (!url || typeof url !== 'string') {
      return url;
    }

    try {
      const urlObj = new URL(url, window.location.href);

      // Check if publisher parameter already exists
      if (urlObj.searchParams.has('publisher')) {
        return url; // Don't overwrite existing publisher param
      }

      // Get the current domain and path (no protocol)
      const publisher = window.location.host + window.location.pathname;

      // Append the publisher parameter
      urlObj.searchParams.append('publisher', publisher);

      return urlObj.toString();
    } catch (e) {
      console.error('Content Unit: Failed to append publisher parameter', e);
      return url; // Return original URL on error
    }
  }

  // Security: Denylist for dangerous redirect-related parameter names
  const BLOCKED_PARAM_KEYS = new Set([
    'redirect', 'redirect_url', 'redirect_uri', 'return_url', 'return_uri',
    'next', 'continue', 'url', 'goto', 'destination', 'target', 'redir',
    'returnto', 'return', 'jump', 'jump_url', 'link', 'linkurl', 'forward'
  ]);

  // Security: Maximum parameters to pass through (DoS prevention)
  const MAX_PASSTHROUGH_PARAMS = 50;

  // Security: Valid parameter key pattern (alphanumeric, underscore, hyphen only)
  const VALID_PARAM_KEY_PATTERN = /^[a-zA-Z0-9_-]+$/;

  /**
   * Appends specified query string parameters from the current page URL to a click URL.
   * Parameters are pulled from window.location.search and appended to the target URL.
   * If a parameter already exists in the click URL, it will be overwritten with the page value.
   * Use ["*"] to pass all query parameters from the page URL.
   * Security: Dangerous redirect parameters are blocked, parameter count is limited.
   * @param {string} url - The click URL to append parameters to
   * @param {string[]} paramKeys - Array of parameter keys to passthrough from page URL, or ["*"] for all
   * @returns {string} URL with passthrough parameters appended
   */
  function appendPassthroughParams(url: string, paramKeys?: string[]): string {
    if (!url || typeof url !== 'string') {
      return url;
    }
    if (!paramKeys || paramKeys.length === 0) {
      return url;
    }

    try {
      const pageParams = new URLSearchParams(window.location.search);
      const targetUrl = new URL(url, window.location.href);

      /**
       * Validates and sets a parameter if it passes security checks
       */
      const safeSetParam = (key: string, value: string): boolean => {
        // Validate key format (alphanumeric, underscore, hyphen only)
        if (!VALID_PARAM_KEY_PATTERN.test(key)) {
          if (__DEV__) {
            console.warn('Content Unit: Skipping invalid param key:', key);
          }
          return false;
        }
        // Block dangerous redirect-related parameters
        if (BLOCKED_PARAM_KEYS.has(key.toLowerCase())) {
          if (__DEV__) {
            console.warn('Content Unit: Blocked dangerous param key:', key);
          }
          return false;
        }
        targetUrl.searchParams.set(key, value);
        return true;
      };

      // Check for wildcard - pass all params (with security limits)
      if (paramKeys.includes('*')) {
        let paramCount = 0;
        pageParams.forEach((value, key) => {
          if (paramCount >= MAX_PASSTHROUGH_PARAMS) {
            return;
          }
          if (safeSetParam(key, value)) {
            paramCount++;
          }
        });
      } else {
        for (const key of paramKeys) {
          const value = pageParams.get(key);
          if (value !== null) {
            safeSetParam(key, value);
          }
        }
      }

      return targetUrl.toString();
    } catch (e) {
      if (__DEV__) {
        console.error('Content Unit: Failed to append passthrough params', e);
      }
      return url;
    }
  }

  /**
   * Safely dispatches a custom event with error handling
   * @param {HTMLElement} element - The element to dispatch from
   * @param {string} eventName - Name of the event
   * @param {Object} detail - Event detail object
   */
  function dispatchSafeEvent(element: HTMLElement, eventName: string, detail: ContentUnitLoadedDetail | ContentUnitClickedDetail): void {
    try {
      element.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
    } catch (e) {
      console.error('Content Unit: Failed to dispatch event', eventName, e);
    }
  }

  /**
   * Validates configuration structure comprehensively.
   * Uses `unknown` type because config comes from external source
   * and must be validated at runtime before being trusted.
   * @param {unknown} config - Configuration object to validate
   * @returns {boolean} True if valid, false otherwise
   */
  function validateConfig(config: unknown): config is Config {
    // Check basic structure
    if (!config || typeof config !== 'object') {
      return false;
    }

    // Type assertion after basic object check for property access
    const cfg = config as Record<string, unknown>;

    // Validate units array
    if (!cfg.units || !Array.isArray(cfg.units)) {
      return false;
    }

    // Validate settings object
    if (!cfg.settings || typeof cfg.settings !== 'object') {
      return false;
    }

    const settings = cfg.settings as Record<string, unknown>;

    // Validate settings properties
    if (typeof settings.lazyLoad !== 'boolean') {
      return false;
    }

    // Validate settings.passthroughParams if present
    if (settings.passthroughParams !== undefined) {
      if (!Array.isArray(settings.passthroughParams)) {
        return false;
      }
      if (!settings.passthroughParams.every((p: unknown) => typeof p === 'string' && p.length > 0)) {
        return false;
      }
    }

    // Validate each unit
    return cfg.units.every((unit: unknown) => {
      if (!unit || typeof unit !== 'object') {
        return false;
      }

      const u = unit as Record<string, unknown>;

      // Unit must have slot and creatives
      if (typeof u.slot !== 'string' || !u.slot) {
        return false;
      }

      if (!Array.isArray(u.creatives) || u.creatives.length === 0) {
        return false;
      }

      // Validate unit.passthroughParams if present
      if (u.passthroughParams !== undefined) {
        if (!Array.isArray(u.passthroughParams)) {
          return false;
        }
        if (!u.passthroughParams.every((p: unknown) => typeof p === 'string' && p.length > 0)) {
          return false;
        }
      }

      // Validate each creative
      return u.creatives.every((creative: unknown) => {
        if (!creative || typeof creative !== 'object') {
          return false;
        }

        const c = creative as Record<string, unknown>;

        return (
          c.imageUrl &&
          typeof c.imageUrl === 'string' &&
          c.clickUrl &&
          typeof c.clickUrl === 'string' &&
          (typeof c.width === 'number' || typeof c.width === 'string') &&
          typeof c.height === 'number'
        );
      });
    });
  }

  /**
   * Computes SHA-256 hash of a domain using SubtleCrypto
   * @param {string} domain - The domain to hash
   * @returns {Promise<string>} 16-character hex hash
   */
  async function hashDomain(domain: string): Promise<string> {
    const data = new TextEncoder().encode(domain + __CONFIG_HASH_SALT__);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 16);
  }

  /**
   * Extracts the base domain from a hostname
   * Strips www and other subdomains, returning just the root domain
   * @param {string} hostname - The full hostname (e.g., 'www.example.com', 'app.example.com')
   * @returns {string} The base domain (e.g., 'example.com')
   */
  function getBaseDomain(hostname: string): string {
    const normalized = hostname.toLowerCase();
    const parts = normalized.split('.');

    // Handle localhost and single-part hostnames
    if (parts.length <= 1) {
      return normalized;
    }

    // Return the last two parts (e.g., 'example.com')
    if (parts.length >= 2) {
      return parts.slice(-2).join('.');
    }

    return normalized;
  }

  /**
   * Fetches config from a URL with retry logic
   * @param {string} url - The URL to fetch
   * @returns {Promise<Config | null>} The config or null if not found/invalid
   */
  async function fetchConfigWithRetry(url: string): Promise<Config | null> {
    for (let attempt = 0; attempt < CONFIG_FETCH_RETRIES; attempt++) {
      try {
        const response = await fetch(url);

        // 404 means no config for this domain - don't retry
        if (response.status === 404) {
          return null;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!validateConfig(data)) {
          if (__DEV__) console.error('Content Unit: Invalid config structure');
          return null;
        }

        return data;
      } catch (_e) {
        if (attempt < CONFIG_FETCH_RETRIES - 1) {
          // Exponential backoff
          const delay = CONFIG_FETCH_BASE_DELAY * Math.pow(2, attempt);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    return null;
  }

  /**
   * Loads configuration for the current domain
   * Always uses the base domain (strips subdomains) for a single request
   * @returns {Promise<Config | null>} The config or null if not found
   */
  async function loadConfig(): Promise<Config | null> {
    const hostname = window.location.hostname;
    const baseDomain = getBaseDomain(hostname);
    const hash = await hashDomain(baseDomain);
    const url = `${CONFIG_BASE_URL}/configs/${hash}.json`;

    if (__DEV__) {
      console.log('Content Unit: Loading config', {
        hostname,
        baseDomain,
        hash,
        configBaseUrl: CONFIG_BASE_URL,
        url
      });
    }

    const config = await fetchConfigWithRetry(url);

    if (config) {
      if (__DEV__) {
        console.log('Content Unit: Configuration loaded successfully', {
          baseDomain,
          units: config.units.length,
          lazyLoad: config.settings.lazyLoad,
          version: '__VERSION__'
        });
      }

      // Validate URLs at config load time
      config.units.forEach(unit => {
        unit.creatives.forEach(creative => {
          if (!isValidUrl(creative.imageUrl)) {
            console.warn('Content Unit: Invalid image URL in config', {
              slot: unit.slot,
              imageUrl: creative.imageUrl
            });
          }
          if (!isValidUrl(creative.clickUrl)) {
            console.warn('Content Unit: Invalid click URL in config', {
              slot: unit.slot,
              clickUrl: creative.clickUrl
            });
          }
        });
      });

      return config;
    }

    // No config found for this domain
    if (__DEV__) {
      console.warn('Content Unit: No configuration found for domain', { hostname, baseDomain });
    }

    return null;
  }

  // Queue for storing requests before script is ready
  window.contentUnits = window.contentUnits || [];

  // Configuration (loaded async)
  let config: Config | null = null;

  // Initialization guard
  let initialized = false;

  /**
   * Intersection Observer for lazy loading
   */
  let observer: IntersectionObserver | null = null;
  const observedElements = new WeakSet<HTMLElement>();

  /**
   * ResizeObserver for responsive creative swapping
   */
  let resizeObserver: ResizeObserver | null = null;
  const resizeObservedElements = new WeakSet<HTMLElement>();

  /**
   * State tracking for responsive slots
   * Maps slot elements to their current creative and configuration
   */
  interface ResponsiveSlotState {
    currentCreative: Creative | null;
    unit: ContentUnit;
    slot: ParsedSlot;
    lastWidth: number;
    debounceTimer: ReturnType<typeof setTimeout> | null;
  }
  const responsiveSlotStates = new WeakMap<HTMLElement, ResponsiveSlotState>();

  /**
   * Debounce delay for resize events (ms)
   */
  const RESIZE_DEBOUNCE_DELAY = 150;

  /**
   * Sets up the IntersectionObserver for lazy loading
   * @param {Config} cfg - The loaded configuration
   */
  function setupLazyLoading(cfg: Config): void {
    if (cfg.settings.lazyLoad && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            // Check if already loaded to prevent race condition
            if (!element.dataset.contentLoaded) {
              // Set flag IMMEDIATELY to prevent race condition with rapid scroll events
              element.dataset.contentLoaded = 'true';

              if (__DEV__) {
                console.log('Content Unit: Slot became visible (lazy load triggered)', {
                  slot: element.dataset.cuSlot
                });
              }

              try {
                loadSlot(element);
                // Clean up observer for this element
                observer?.unobserve(element);
              } catch (e) {
                if (__DEV__) console.error('Content Unit: Failed to load content', e);
                // Don't set flag if loading failed - allow retry
              }
            }
          }
        });
      }, {
        rootMargin: `${cfg.settings.lazyLoadMargin || DEFAULT_LAZY_LOAD_MARGIN}px`
      });
    } else if (cfg.settings.lazyLoad && !('IntersectionObserver' in window)) {
      if (__DEV__) {
        console.warn('Content Unit: IntersectionObserver not available, lazy loading disabled. Ads will load immediately.');
      }
    }
  }

  /**
   * Handles responsive creative swapping when container size changes
   * @param {HTMLElement} element - The slot element being resized
   */
  function handleResponsiveResize(element: HTMLElement): void {
    const state = responsiveSlotStates.get(element);
    if (!state) return;

    // Clear any pending debounce timer
    if (state.debounceTimer) {
      clearTimeout(state.debounceTimer);
    }

    // Debounce the resize handling
    state.debounceTimer = setTimeout(() => {
      const containerWidth = element.offsetWidth;

      // Skip if width hasn't meaningfully changed (within 5px tolerance)
      if (Math.abs(containerWidth - state.lastWidth) < 5) {
        return;
      }

      if (__DEV__) {
        console.log('Content Unit: Container resized', {
          slot: state.slot.slot,
          oldWidth: state.lastWidth,
          newWidth: containerWidth
        });
      }

      // Compute max height from slot.height in responsive mode
      let maxHeight: number | null = null;
      if (state.slot.height) {
        const parsed = parseInt(state.slot.height, 10);
        if (!isNaN(parsed) && parsed > 0) {
          maxHeight = parsed;
        }
      }

      // Find the best creative for the new container size
      const newCreative = findBestCreativeForContainer(
        state.unit,
        containerWidth,
        maxHeight
      );

      // Check if we need to swap creatives
      if (newCreative && newCreative !== state.currentCreative) {
        if (__DEV__) {
          console.log('Content Unit: Swapping creative', {
            slot: state.slot.slot,
            from: state.currentCreative ? `${state.currentCreative.width}x${state.currentCreative.height}` : 'none',
            to: `${newCreative.width}x${newCreative.height}`
          });
        }

        // Update state
        state.currentCreative = newCreative;
        state.lastWidth = containerWidth;

        // Re-render with the new creative
        renderResponsiveContent(element, state.slot, state.unit, newCreative, containerWidth);
      } else {
        state.lastWidth = containerWidth;
      }
    }, RESIZE_DEBOUNCE_DELAY);
  }

  // Initialize ResizeObserver for responsive slots
  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(entry => {
        const element = entry.target as HTMLElement;
        handleResponsiveResize(element);
      });
    });
  }

  /**
   * Parse slot element and extract configuration
   * @param {HTMLElement} element - The slot DOM element
   * @returns {Object} Parsed slot configuration
   */
  function parseSlot(element: HTMLElement): ParsedSlot | null {
    // Validate slot ID before processing
    const validatedSlotId = validateSlotId(element.dataset.cuSlot);

    if (!validatedSlotId) {
      if (__DEV__) console.warn('Content Unit: Invalid or missing slot ID', { rawSlotId: element.dataset.cuSlot });
      return null;
    }

    return {
      element: element,
      slot: validatedSlotId,
      format: element.dataset.cuFormat || 'auto',
      width: element.dataset.cuWidth || null,
      height: element.dataset.cuHeight || null,
      responsive: element.dataset.fullWidthResponsive === 'true'
    };
  }

  /**
   * Load and display content
   * @param {HTMLElement} element - The slot DOM element
   */
  function loadSlot(element: HTMLElement): void {
    if (!config) {
      if (__DEV__) console.warn('Content Unit: Config not loaded, cannot render slot');
      return;
    }

    try {
      const slot = parseSlot(element);

      // Skip if slot validation failed
      if (!slot) {
        return;
      }

      if (__DEV__) {
        console.log('Content Unit: Processing slot', {
          slot: slot.slot,
          width: slot.width,
          height: slot.height,
          responsive: slot.responsive,
          format: slot.format
        });
      }

      // Find matching unit configuration
      const unit = config.units.find(u => u.slot === slot.slot);

      if (!unit) {
        if (__DEV__) console.warn(`Content Unit: No configuration found for slot ${slot.slot}`);
        return;
      }

      if (__DEV__) {
        console.log('Content Unit: Unit found', {
          slot: slot.slot,
          availableCreatives: unit.creatives.length
        });
      }

      // Render the content
      renderContent(element, slot, unit);
    } catch (e) {
      if (__DEV__) console.error('Content Unit: Error loading content', e);
      // Continue execution to allow other slots to load
    }
  }

  /**
   * Normalizes size values for consistent comparison
   * @param {number | string} size - The size to normalize
   * @returns {number | "100%"} Normalized size
   */
  function normalizeSize(size: number | string): number | "100%" {
    if (size === "100%") return "100%";
    return typeof size === "string" ? parseInt(size, 10) : size;
  }

  /**
   * Finds the best matching creative for the requested dimensions
   * @param {ContentUnit} unit - The unit configuration containing creatives
   * @param {number | string} width - Requested width
   * @param {number | string} height - Requested height
   * @returns {Creative | null} The matching creative or null if none found
   */
  function findMatchingCreative(unit: ContentUnit, width: number | string, height: number | string): Creative | null {
    if (!unit.creatives || unit.creatives.length === 0) {
      return null;
    }

    // Normalize sizes for consistent comparison
    const normalizedWidth = normalizeSize(width);
    const normalizedHeight = normalizeSize(height);

    // Look for exact match
    const exactMatch = unit.creatives.find(c =>
      normalizeSize(c.width) === normalizedWidth &&
      normalizeSize(c.height) === normalizedHeight
    );

    if (exactMatch) {
      if (__DEV__) {
        console.log('Content Unit: Creative matched', {
          size: `${exactMatch.width}x${exactMatch.height}`,
          imageUrl: exactMatch.imageUrl
        });
      }
      return exactMatch;
    }

    // Fallback to first creative if no match
    const fallback = unit.creatives[0];
    if (__DEV__) {
      console.log('Content Unit: No exact creative match, using fallback', {
        requestedSize: `${width}x${height}`,
        fallbackSize: `${fallback.width}x${fallback.height}`
      });
    }
    return fallback;
  }

  /**
   * Finds the best creative for a given container width, with optional max height constraint.
   * Prioritizes the largest creative width that fits within the container.
   * @param {ContentUnit} unit - The unit configuration containing creatives
   * @param {number} containerWidth - The current container width in pixels
   * @param {number | null} maxHeight - Optional maximum height constraint
   * @returns {Creative | null} The best matching creative or null if none found
   */
  function findBestCreativeForContainer(
    unit: ContentUnit,
    containerWidth: number,
    maxHeight: number | null
  ): Creative | null {
    if (!unit.creatives || unit.creatives.length === 0) {
      return null;
    }

    // Filter creatives that fit within the container width
    // For "100%" width creatives, they always fit
    const fittingCreatives = unit.creatives.filter(c => {
      const creativeWidth = c.width === '100%' ? 0 : (typeof c.width === 'number' ? c.width : parseInt(c.width, 10));
      const widthFits = creativeWidth <= containerWidth;

      // If max height is specified, filter by height too
      if (maxHeight !== null) {
        return widthFits && c.height <= maxHeight;
      }

      return widthFits;
    });

    if (fittingCreatives.length === 0) {
      // No creatives fit width - try to find one that at least fits height constraint
      let fallbackCandidates = unit.creatives;

      if (maxHeight !== null) {
        const heightFitting = unit.creatives.filter(c => c.height <= maxHeight);
        if (heightFitting.length > 0) {
          fallbackCandidates = heightFitting;
        }
      }

      // Sort by width ascending (smallest first)
      const sorted = [...fallbackCandidates].sort((a, b) => {
        const widthA = a.width === '100%' ? 0 : (typeof a.width === 'number' ? a.width : parseInt(a.width, 10));
        const widthB = b.width === '100%' ? 0 : (typeof b.width === 'number' ? b.width : parseInt(b.width, 10));
        return widthA - widthB;
      });

      if (__DEV__) {
        console.log('Content Unit: No creatives fit container, using smallest', {
          containerWidth,
          maxHeight,
          fallback: `${sorted[0].width}x${sorted[0].height}`
        });
      }

      return sorted[0];
    }

    // Sort fitting creatives by width descending (largest first)
    // This ensures we use the largest creative that fits
    const sorted = fittingCreatives.sort((a, b) => {
      const widthA = a.width === '100%' ? containerWidth : (typeof a.width === 'number' ? a.width : parseInt(a.width, 10));
      const widthB = b.width === '100%' ? containerWidth : (typeof b.width === 'number' ? b.width : parseInt(b.width, 10));
      return widthB - widthA;
    });

    const best = sorted[0];

    if (__DEV__) {
      console.log('Content Unit: Best creative for container', {
        containerWidth,
        maxHeight,
        selected: `${best.width}x${best.height}`,
        candidates: fittingCreatives.length
      });
    }

    return best;
  }

  /**
   * Creates the ad link element with image and event handlers
   * @param {Creative} creative - The creative to render
   * @param {ContentUnit} unit - The unit configuration
   * @param {HTMLElement} element - The slot element for event dispatching
   * @param {ParsedSlot} slot - The parsed slot configuration
   * @param {HTMLElement} container - The container element
   * @returns {HTMLAnchorElement} The created link element
   */
  function createAdElement(
    creative: Creative,
    unit: ContentUnit,
    element: HTMLElement,
    slot: ParsedSlot,
    container: HTMLElement
  ): HTMLAnchorElement {
    const link = document.createElement('a');
    // Check if autoAppendPublisher is enabled (defaults to true)
    const shouldAppendPublisher = unit.autoAppendPublisher !== false;
    // Resolve passthroughParams: unit-level overrides settings-level
    const passthroughParams = unit.passthroughParams ?? config?.settings?.passthroughParams;

    let finalUrl = creative.clickUrl;
    if (shouldAppendPublisher) {
      finalUrl = appendPublisherParam(finalUrl);
    }
    finalUrl = appendPassthroughParams(finalUrl, passthroughParams);
    link.href = finalUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.cssText = 'display:block;width:100%;height:100%;text-decoration:none;';

    // Add loading overlay
    const loadingOverlay = document.createElement('div');
    loadingOverlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;background:#f5f5f5;';
    container.appendChild(loadingOverlay);

    const img = document.createElement('img');
    img.alt = sanitizeText(creative.alt || 'Content Unit');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;opacity:0;transition:opacity 0.2s ease-in;';

    // Show image and remove loading overlay on successful load
    img.onload = function(): void {
      img.style.opacity = '1';
      loadingOverlay.remove();
    };

    // Add error handler for failed image loads with retry mechanism
    let retryCount = 0;
    img.onerror = function(): void {
      if (retryCount < IMAGE_RETRY_MAX) {
        retryCount++;
        if (__DEV__) {
          console.log('Content Unit: Retrying image load', {
            url: creative.imageUrl,
            attempt: retryCount
          });
        }
        // Exponential backoff: 1s, 2s
        setTimeout(() => {
          // Add cache-busting parameter for retry
          const separator = creative.imageUrl.includes('?') ? '&' : '?';
          img.src = creative.imageUrl + separator + '_retry=' + retryCount;
        }, retryCount * IMAGE_RETRY_DELAY_MS);
      } else {
        if (__DEV__) {
          console.error('Content Unit: Failed to load image after retries:', creative.imageUrl);
        }
        // Collapse the unit completely - show nothing
        element.style.display = 'none';
      }
    };

    // Set src after handlers are attached
    img.src = creative.imageUrl;

    link.appendChild(img);

    // Track click event
    link.addEventListener('click', function(): void {
      dispatchSafeEvent(element, 'content-unit-clicked', {
        slot: slot.slot,
        creative: creative
      });
    });

    return link;
  }

  /**
   * Render responsive content with a specific creative
   * Used for both initial render and creative swaps
   * @param {HTMLElement} element - The slot DOM element
   * @param {ParsedSlot} slot - Parsed slot configuration
   * @param {ContentUnit} unit - Unit configuration
   * @param {Creative} creative - The creative to render
   * @param {number} containerWidth - Current container width
   */
  function renderResponsiveContent(
    element: HTMLElement,
    slot: ParsedSlot,
    unit: ContentUnit,
    creative: Creative,
    containerWidth: number
  ): void {
    try {
      // Use the creative's actual dimensions for proper aspect ratio
      const creativeWidth = creative.width === '100%' ? containerWidth :
        (typeof creative.width === 'number' ? creative.width : parseInt(creative.width, 10));
      const creativeHeight = creative.height;

      // Create container that fills width and maintains aspect ratio
      const container = document.createElement('div');
      container.className = 'content-unit-responsive-container';
      container.style.cssText = `width:100%;max-width:${creativeWidth}px;height:auto;display:block;overflow:hidden;position:relative;margin:0 auto;`;

      if (creative.imageUrl && creative.clickUrl) {
        // Validate URLs before using them (XSS prevention)
        if (!isValidUrl(creative.imageUrl)) {
          console.error('Content Unit: Invalid image URL detected', creative.imageUrl);
          element.style.display = 'none';
          return;
        } else if (!isValidUrl(creative.clickUrl)) {
          console.error('Content Unit: Invalid click URL detected', creative.clickUrl);
          element.style.display = 'none';
          return;
        } else {
          // Create the ad element with responsive image
          const link = document.createElement('a');
          const shouldAppendPublisher = unit.autoAppendPublisher !== false;
          // Resolve passthroughParams: unit-level overrides settings-level
          const passthroughParams = unit.passthroughParams ?? config?.settings?.passthroughParams;

          let finalUrl = creative.clickUrl;
          if (shouldAppendPublisher) {
            finalUrl = appendPublisherParam(finalUrl);
          }
          finalUrl = appendPassthroughParams(finalUrl, passthroughParams);
          link.href = finalUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.cssText = 'display:block;width:100%;text-decoration:none;';

          // Add loading overlay (use aspect ratio for responsive)
          const loadingOverlay = document.createElement('div');
          loadingOverlay.style.cssText = `position:absolute;top:0;left:0;width:100%;height:0;padding-bottom:${(creativeHeight / creativeWidth) * 100}%;background:#f5f5f5;`;
          container.appendChild(loadingOverlay);

          const img = document.createElement('img');
          img.alt = sanitizeText(creative.alt || 'Content Unit');
          img.loading = 'lazy';
          img.decoding = 'async';
          img.style.cssText = 'width:100%;height:auto;display:block;opacity:0;transition:opacity 0.2s ease-in;';

          // Show image and remove loading overlay on successful load
          img.onload = function(): void {
            img.style.opacity = '1';
            loadingOverlay.remove();
          };

          // Add error handler for failed image loads with retry mechanism
          let retryCount = 0;
          img.onerror = function(): void {
            if (retryCount < IMAGE_RETRY_MAX) {
              retryCount++;
              if (__DEV__) {
                console.log('Content Unit: Retrying image load', {
                  url: creative.imageUrl,
                  attempt: retryCount
                });
              }
              // Exponential backoff: 1s, 2s
              setTimeout(() => {
                // Add cache-busting parameter for retry
                const separator = creative.imageUrl.includes('?') ? '&' : '?';
                img.src = creative.imageUrl + separator + '_retry=' + retryCount;
              }, retryCount * IMAGE_RETRY_DELAY_MS);
            } else {
              if (__DEV__) {
                console.error('Content Unit: Failed to load image after retries:', creative.imageUrl);
              }
              // Collapse the unit completely - show nothing
              element.style.display = 'none';
            }
          };

          // Set src after handlers are attached
          img.src = creative.imageUrl;

          link.appendChild(img);

          // Track click event
          link.addEventListener('click', function(): void {
            dispatchSafeEvent(element, 'content-unit-clicked', {
              slot: slot.slot,
              creative: creative
            });
          });

          container.appendChild(link);
        }
      } else {
        // No valid creative - collapse the unit
        element.style.display = 'none';
        return;
      }

      // Clear existing content safely and append
      clearElement(element);
      element.appendChild(container);

      // Dispatch custom event for tracking
      dispatchSafeEvent(element, 'content-unit-loaded', {
        slot: slot.slot,
        unit: unit,
        creative: creative
      });

      if (__DEV__) {
        console.log('Content Unit: Rendered responsive content', {
          slot: slot.slot,
          creative: `${creative.width}x${creative.height}`,
          containerWidth: containerWidth
        });
      }
    } catch (e) {
      console.error('Content Unit: Error rendering responsive content', e);
    }
  }

  /**
   * Render content
   * @param {HTMLElement} element - The slot DOM element
   * @param {Object} slot - Parsed slot configuration
   * @param {Object} unit - Unit configuration
   */
  function renderContent(element: HTMLElement, slot: ParsedSlot, unit: ContentUnit): void {
    try {
      // Handle responsive mode with creative swapping
      if (slot.responsive) {
        const containerWidth = element.offsetWidth || DEFAULT_WIDTH;

        // In responsive mode, data-cu-height acts as max height constraint
        let maxHeight: number | null = null;
        if (slot.height) {
          const parsed = parseInt(slot.height, 10);
          if (!isNaN(parsed) && parsed > 0) {
            maxHeight = parsed;
          }
        }

        // Find the best creative for current container size
        const creative = findBestCreativeForContainer(unit, containerWidth, maxHeight);

        if (!creative) {
          if (__DEV__) console.warn('Content Unit: No creative found for responsive slot', slot.slot);
          return;
        }

        // Store state for resize handling
        responsiveSlotStates.set(element, {
          currentCreative: creative,
          unit: unit,
          slot: slot,
          lastWidth: containerWidth,
          debounceTimer: null
        });

        // Set up ResizeObserver for this element
        if (resizeObserver && !resizeObservedElements.has(element)) {
          resizeObservedElements.add(element);
          resizeObserver.observe(element);

          if (__DEV__) {
            console.log('Content Unit: ResizeObserver attached for responsive slot', {
              slot: slot.slot,
              initialWidth: containerWidth
            });
          }
        }

        // Render the initial creative
        renderResponsiveContent(element, slot, unit, creative, containerWidth);
        return;
      }

      // Non-responsive mode: Calculate dimensions
      let width: number | string = slot.width || DEFAULT_WIDTH;
      let height: number | string = slot.height || DEFAULT_HEIGHT;

      // Convert string dimensions to numbers if needed
      if (typeof width === 'string' && width !== '100%') {
        width = parseInt(width, 10);
      }
      if (typeof height === 'string') {
        height = parseInt(height, 10);
      }

      // Find matching creative for this size
      const creative = findMatchingCreative(unit, width, height);

      // Create container
      const container = document.createElement('div');
      container.style.cssText = `width:${formatDimension(width)};height:${formatDimension(height)};display:block;overflow:hidden;position:relative;`;

      if (creative && creative.imageUrl && creative.clickUrl) {
        // Validate URLs before using them (XSS prevention)
        if (!isValidUrl(creative.imageUrl)) {
          console.error('Content Unit: Invalid image URL detected', creative.imageUrl);
          element.style.display = 'none';
          return;
        } else if (!isValidUrl(creative.clickUrl)) {
          console.error('Content Unit: Invalid click URL detected', creative.clickUrl);
          element.style.display = 'none';
          return;
        } else {
          // Create and append the ad element
          const link = createAdElement(creative, unit, element, slot, container);
          container.appendChild(link);
        }
      } else {
        // No valid creative - collapse the unit
        element.style.display = 'none';
        return;
      }

      // Clear existing content safely and append
      clearElement(element);
      element.appendChild(container);

      // Dispatch custom event for tracking
      dispatchSafeEvent(element, 'content-unit-loaded', {
        slot: slot.slot,
        unit: unit,
        creative: creative || undefined
      });

      if (__DEV__) {
        console.log('Content Unit: Rendered successfully', {
          slot: slot.slot,
          finalSize: `${formatDimension(width)} × ${formatDimension(height)}`,
          hasCreative: !!creative
        });
      }
    } catch (e) {
      console.error('Content Unit: Error rendering content', e);
    }
  }

  /**
   * Process a single request
   * @param {Object} _request - Request configuration
   */
  function processRequest(_request: Record<string, unknown> = {}): void {
    if (!config) {
      if (__DEV__) console.warn('Content Unit: Config not loaded, skipping request');
      return;
    }

    try {
      // Find all slots that haven't been processed
      const slots = document.querySelectorAll<HTMLElement>(SLOT_SELECTOR);

      if (__DEV__) {
        console.log('Content Unit: Found slots in DOM', {
          count: slots.length,
          lazyLoadEnabled: config.settings.lazyLoad
        });
      }

      slots.forEach(element => {
        if (config!.settings.lazyLoad && observer) {
          // Prevent duplicate observations
          if (!observedElements.has(element)) {
            observedElements.add(element);
            observer.observe(element);
            if (__DEV__) {
              console.log('Content Unit: Slot registered for lazy loading', {
                slot: element.dataset.cuSlot
              });
            }
          }
        } else {
          loadSlot(element);
          element.dataset.contentLoaded = 'true';
        }
      });
    } catch (e) {
      if (__DEV__) console.error('Content Unit: Error processing request', e);
    }
  }

  /**
   * Initialize the system after config is loaded
   * @param {Config} cfg - The loaded configuration
   */
  function initWithConfig(cfg: Config): void {
    config = cfg;

    // Set up lazy loading
    setupLazyLoading(cfg);

    try {
      if (__DEV__) console.log('Content Unit: Initializing system');

      // Process any queued requests
      const queue = Array.isArray(window.contentUnits) ? window.contentUnits.slice() : [];

      if (__DEV__) {
        console.log('Content Unit: Processing queued requests', {
          queuedRequests: queue.length
        });
      }

      // Replace the array with our push handler
      window.contentUnits = {
        push: function(request: Record<string, unknown>): void {
          processRequest(request);
        }
      };

      // Process queued requests
      queue.forEach(request => processRequest(request));

      // Initial scan for slots
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          processRequest();
        });
      } else {
        processRequest();
      }
    } catch (e) {
      if (__DEV__) console.error('Content Unit: Initialization error', e);
    }
  }

  /**
   * Initialize the system
   */
  async function init(): Promise<void> {
    // Prevent double initialization
    if (initialized) {
      if (__DEV__) console.warn('Content Unit: Already initialized');
      return;
    }
    initialized = true;

    try {
      // Load configuration
      const cfg = await loadConfig();

      if (!cfg) {
        // No config for this domain - silent fail
        window.__contentUnitsConfigError__ = true;

        // Set up a no-op push handler so calls don't throw errors
        window.contentUnits = {
          push: function(_request: Record<string, unknown>): void {
            // No-op: no config for this domain
          }
        };

        if (__DEV__) {
          console.warn('Content Unit: No configuration available for this domain. Ads will not be displayed.');
        }
        return;
      }

      initWithConfig(cfg);
    } catch (e) {
      if (__DEV__) console.error('Content Unit: Initialization error', e);
      window.__contentUnitsConfigError__ = true;
    }
  }

  // Start the system
  init();

})(window, document);
